import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowClockwiseIcon, CheckCircleIcon, HouseIcon } from '@phosphor-icons/react';
import { LoaderOne } from '../components/ui/loader';
import AnalysisConfirmationModal from '../components/modals/AnalysisConfirmationModal';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

// Open-redirect guard, same posture frontend-migration's own returnTo validator uses: parse with
// NO base URL, so a schemeless value like "app.quizrr.in/challenge" throws (the WHATWG parser would
// otherwise treat it as a relative path against a base and wrongly pass as same-origin) instead of
// being silently accepted and later mis-resolved by the browser. Only http/https, and only a
// same-origin/quizrr.in/localhost host, is allowed through.
const isAllowedReturnTo = (value) => {
  if (!value) return false;
  if (value.startsWith('/') && !value.startsWith('//')) return true; // unambiguous same-origin path
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    const host = url.hostname;
    return (
      host === window.location.hostname ||
      host === 'quizrr.in' ||
      host.endsWith('.quizrr.in') ||
      host === 'localhost' ||
      host === '127.0.0.1'
    );
  } catch {
    return false;
  }
};

const TestSubmission = () => {
  const { pack, test } = useParams();
  const query = useQuery();
  const type = query.get('type');
  // Set by whatever launched this attempt (e.g. Challenge99Home's handleStart, forwarded through
  // the test-taking flow) - already a full, origin-qualified URL. The confirmation flow below
  // (poll -> "Test Submitted" -> modal) always runs the same for every student either way; this
  // only swaps the final destination once they're done with it. Validated so an untrusted/malformed
  // value can't become an open redirect.
  const rawReturnTo = query.get('returnTo');
  const returnTo = isAllowedReturnTo(rawReturnTo) ? rawReturnTo : null;
  const navigate = useNavigate();
  const [showHomeButton, setShowHomeButton] = useState(false);
  const [submissionCheckCount, setSubmissionCheckCount] = useState(0);
  const [isSubmissionInProgress, setIsSubmissionInProgress] = useState(false);
  const [analysisConfirmationOpen, setAnalysisConfirmationOpen] = useState(false);
  const [analysisUrl, setAnalysisUrl] = useState('');
  const handledSubmittedRef = useRef(false);
  const intervalRef = useRef(null);

  // returnTo can be a different origin than this page - react-router's navigate() only handles
  // in-app paths, so a cross-origin destination has to go through a real browser navigation.
  const goTo = (url) => {
    if (returnTo) {
      window.location.href = url;
    } else {
      navigate(url);
    }
  };

  const checkSubmissionStatus = async () => {
    setSubmissionCheckCount((c) => c + 1);
    try {
      const res = await axios.get(`/api/v2/test/${pack}/${test}/check-submission`);
      const { isSubmitted, isSubmissionInProgress, otherTestsAttempted } = res?.data?.data || {};
      setIsSubmissionInProgress(isSubmissionInProgress);

      if (isSubmitted) {
        setShowHomeButton(false);
        if (handledSubmittedRef.current) return;
        handledSubmittedRef.current = true;

        const url = returnTo || `/${pack}/${test}/analysis?source=test_submission`;

        if (!otherTestsAttempted) {
          setAnalysisUrl(url);
          setAnalysisConfirmationOpen(true);
          return;
        }

        goTo(url);
      }
    } catch (error) {
      console.error('Error checking submission status:', error);
    }
  };

  useEffect(() => {
    checkSubmissionStatus(); // initial call

    intervalRef.current = setInterval(() => {
      checkSubmissionStatus();
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [pack, test]);

  useEffect(() => {
    if (submissionCheckCount > 3) setShowHomeButton(true);
  }, [submissionCheckCount]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white px-4 relative">
      <div className="text-center max-w-2xl mx-auto">
        <h1 className="flex items-center justify-center gap-3 text-4xl sm:text-5xl font-semibold text-gray-900">
          {type === 'early' ? 'Test Completed' : 'Test Submitted'}
          <CheckCircleIcon size={50} weight="fill" className="text-green-600" />
        </h1>

        <p className="mt-2 text-lg font-medium text-gray-700">
          {type === 'early'
            ? 'This test has been submitted already.'
            : "Good things take time! We're generating your detailed analysis."}
        </p>

        {isSubmissionInProgress && (
          <div className="mt-5 flex flex-col items-center justify-center gap-10">
            <LoaderOne />
            {showHomeButton && (
              <button
                onClick={() => (window.location.href = '/v2/home')}
                className="cursor-pointer flex flex-row items-center justify-center gap-2 bg-white hover:bg-gray-100 text-gray-800 border border-gray-300 text-lg px-6 py-3 rounded-lg transition-colors duration-200"
              >
                <HouseIcon size={22} />
                Go Home
              </button>
            )}
          </div>
        )}
      </div>

      <div className="absolute bottom-4 w-full flex justify-center">
        <p className="text-sm text-gray-500">It'll just take a few minutes. You'll get an email once it's ready</p>
      </div>

      <AnalysisConfirmationModal
        isOpen={analysisConfirmationOpen}
        onClose={() => {
          setAnalysisConfirmationOpen(false);
          navigate(`/v3/${pack}/tests?source=test_submission`);
        }}
        onConfirm={() => {
          setAnalysisConfirmationOpen(false);
          goTo(analysisUrl || returnTo || `/${pack}/${test}/analysis?source=test_submission`);
        }}
      />
    </div>
  );
};

export default TestSubmission;
